import { defineStore } from 'pinia'
import { useCoreSocket } from '@/services/socket/core-socket'
import type { BuildingTypeDirectoryEntry } from '@/helpers/building-type-availability'

export type HelpText = Map<string, { title: string; body: string }>
export type WidgetTexts = Map<string, Record<string, unknown>>
export type BuildingTypeOption = Omit<BuildingTypeDirectoryEntry, 'usageType'> & { usageType: string }

export interface State {
  helpText: HelpText
  widgetTexts: WidgetTexts
  buildingTypes: BuildingTypeOption[]
  /** False until the socket delivered the building types (also after a language reset). */
  buildingTypesLoaded: boolean
  pendingWidgetTextRequests: Set<string>
}

function cacheKey(slug: string, role?: string): string {
  return role ? `${slug}:${role}` : slug
}

function getValueFromPath(texts: Record<string, unknown>, pathParts: string[]): unknown {
  let current: unknown = texts
  for (const part of pathParts) {
    if (typeof current !== 'object' || current === null) return null
    current = (current as Record<string, unknown>)[part]
  }
  return current
}

export const useContentStore = defineStore('content', {
  state: (): State => ({
    helpText: new Map() as HelpText,
    widgetTexts: new Map() as WidgetTexts,
    buildingTypes: [],
    buildingTypesLoaded: false,
    pendingWidgetTextRequests: new Set<string>(),
  }),
  getters: {
    getHelpText: (state) => (slug: string, role?: string) => {
      const key = cacheKey(slug, role)
      if (!state.helpText.get(key)) {
        const data: { slug: string; role?: string } = { slug }
        if (role) data.role = role
        useCoreSocket()?.messageCenter?.triggerMessage('get_help_text', data)
        return null
      } else {
        return state.helpText?.get(key)
      }
    },
    getWidgetText:
      (state) =>
      (slug: string, role: string | undefined, ...pathParts: string[]): string | null => {
        const key = cacheKey(slug, role)
        const texts = state.widgetTexts.get(key)
        if (!texts && !state.pendingWidgetTextRequests.has(key)) {
          state.pendingWidgetTextRequests.add(key)
          const data: { slug: string; role?: string } = { slug }
          if (role) data.role = role
          useCoreSocket()?.messageCenter?.triggerMessage('get_widget_texts', data)
        }
        const baseTexts = state.widgetTexts.get(slug)
        const current = texts
          ? getValueFromPath(texts, pathParts)
          : baseTexts
            ? getValueFromPath(baseTexts, pathParts)
            : null
        return typeof current === 'string' ? current : null
      },
    getWidgetTextValue:
      (state) =>
      (slug: string, role: string | undefined, ...pathParts: string[]): unknown => {
        const key = cacheKey(slug, role)
        const texts = state.widgetTexts.get(key)
        if (!texts) {
          // Lazy fetch for missing key (role-specific or base), deduped by key.
          if (!state.pendingWidgetTextRequests.has(key)) {
            state.pendingWidgetTextRequests.add(key)
            const data: { slug: string; role?: string } = { slug }
            if (role) data.role = role
            useCoreSocket()?.messageCenter?.triggerMessage('get_widget_texts', data)
          }
          // Fall back to base (no-role) version
          const baseTexts = state.widgetTexts.get(slug)
          if (!baseTexts) return null
          return getValueFromPath(baseTexts, pathParts)
        }
        return getValueFromPath(texts, pathParts)
      },
    getBuildingTypes: (state): BuildingTypeOption[] => state.buildingTypes,
    getBuildingTypeById:
      (state) =>
      (id?: string | null): BuildingTypeOption | null => {
        const normalizedId = id?.trim()
        if (!normalizedId) return null
        return state.buildingTypes.find((item) => item.id === normalizedId) ?? null
      },
    getBuildingTypeNameById:
      (state) =>
      (id?: string | null): string | null => {
        const normalizedId = id?.trim()
        if (!normalizedId) return null
        const matched = state.buildingTypes.find((item) => item.id === normalizedId)
        return matched?.name ?? null
      },
    getBuildingTypesByUsage:
      (state) =>
      (usageType: string): BuildingTypeOption[] =>
        state.buildingTypes.filter((item) => item.usageType === usageType),
  },
  actions: {
    async initHelpText(slug: string, title: string, body: string, role?: string) {
      const key = cacheKey(slug, role)
      this.helpText.set(key, { title, body })
    },
    initWidgetTexts(slug: string, texts: Record<string, unknown>, role?: string) {
      const key = cacheKey(slug, role)
      this.widgetTexts.set(key, texts)
      this.pendingWidgetTextRequests.delete(key)
    },
    initBuildingTypes(items: BuildingTypeOption[]) {
      this.buildingTypes = items
      this.buildingTypesLoaded = true
    },
    clearLocalizedContent() {
      this.helpText = new Map() as HelpText
      this.widgetTexts = new Map() as WidgetTexts
      this.buildingTypes = []
      this.buildingTypesLoaded = false
      this.pendingWidgetTextRequests = new Set<string>()
    },
  },
})
